'use client';

import { useMemo } from 'react';
import { AssetTier, MarketStatus } from '@drift-labs/sdk';
import { ENUM_UTILS } from '@drift/common';
import Button from 'src/components/primitives/Button/Button';
import useModal from 'src/components/Modals/hooks/useModal';
import useDriftClient from 'src/hooks/useDriftClient';
import useTickedAllSubAccounts from 'src/hooks/useTickedAllSubAccounts';
import { useBalanceRowData } from 'src/hooks/useBalanceRowData';
import { BalanceRowData } from './types';

interface ActionsCellProps {
	row: BalanceRowData;
}

export function ActionsCell({ row }: ActionsCellProps) {
	const { spotMarketIndex, highlighted } = row;
	const { show } = useModal();
	const driftClient = useDriftClient();
	const subaccounts = useTickedAllSubAccounts();

	const spotMarketInfo = useBalanceRowData({
		spotMarketIndex,
		highlighted,
	});

	const spotMarketAccount = useMemo(
		() => driftClient?.getSpotMarketAccount(spotMarketIndex),
		[driftClient, spotMarketIndex]
	);

	const isMarketInReduceOnly = useMemo(
		() => ENUM_UTILS.match(spotMarketAccount?.status, MarketStatus.REDUCE_ONLY),
		[spotMarketAccount]
	);

	const marketIsProtected = useMemo(
		() => ENUM_UTILS.match(spotMarketAccount?.assetTier, AssetTier.PROTECTED),
		[spotMarketAccount]
	);

	const userHasAccountInPool = useMemo(
		() => subaccounts.some((acc) => acc?.poolId === spotMarketAccount?.poolId),
		[subaccounts, spotMarketAccount?.poolId]
	);

	if (!spotMarketInfo) return null;

	const borrowDisabled = marketIsProtected || !userHasAccountInPool;
	const isBorrow = spotMarketInfo.borrowsBase.gtZero();
	const hasUserSpotBalance = isBorrow
		? spotMarketInfo.borrowsBase.gtZero()
		: spotMarketInfo.depositsBase.gtZero();

	const displayWithdrawButton = hasUserSpotBalance && !isBorrow;
	const displayRepayButton = hasUserSpotBalance && isBorrow;
	const displayDepositButton = !isMarketInReduceOnly && !displayRepayButton;
	const displayBorrowButton =
		!borrowDisabled && !isMarketInReduceOnly && !displayWithdrawButton;

	return (
		<div className="flex w-full items-center gap-sm">
			{displayDepositButton && (
				<Button
					size="sm"
					variant="secondary"
					className="flex-1"
					onClick={() => {
						show({
							type: 'deposit',
							props: { collateralIndex: spotMarketIndex },
						});
					}}
				>
					Deposit
				</Button>
			)}
			{displayRepayButton && (
				<Button
					size="sm"
					variant="secondary"
					className="flex-1"
					onClick={() => {
						show({
							type: 'repay',
							props: { collateralIndex: spotMarketIndex },
						});
					}}
				>
					Repay
				</Button>
			)}
			{displayWithdrawButton && (
				<Button
					size="sm"
					variant="secondary"
					className="flex-1"
					onClick={() => {
						show({
							type: 'withdraw',
							props: { collateralIndex: spotMarketIndex },
						});
					}}
				>
					Withdraw
				</Button>
			)}
			{displayBorrowButton && (
				<Button
					size="sm"
					variant="secondary"
					className="flex-1"
					onClick={() => {
						show({
							type: 'borrow',
							props: { collateralIndex: spotMarketIndex },
						});
					}}
				>
					Borrow
				</Button>
			)}
		</div>
	);
}
